import { styled } from '@mui/material';
import { useSnackbar } from 'contexts/snackbar';
import { useViewport } from 'contexts/viewport';
import { Fragment, useRef, useState } from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Button,
    Divider,
    Dropdown,
    EmailMultiInput,
    Form,
    Icon,
    IconButton,
    ResponsiveDialog,
    Stack,
    Tabs,
    Text,
} from 'shell/components';
import { downloadCSV, getUserPreferredFullName, parseCSV, splitArrayByCondition } from 'utils/helper';
import { emailRegex } from 'utils/regex';

const CSV_HEADERS = ['E-mail', 'First Name', 'Last Name'];

const StudentInvitations = ({
    open,
    setOpen,
    course,
    accountUsers,
    csvStudents,
    setCSVStudents,
    accordions,
    setAccordions,
    inputValue,
    setInputValue,
    emails,
    setEmails,
    isLoading,
    setIsLoading,
    sendInvitations,
    getMemberActionOptions,
    onSelect,
}) => {
    const { message } = useSnackbar();
    const { device } = useViewport();

    const inputRef = useRef(null);

    const studentAccountUsers = (accountUsers || []).filter(({ role }) => role === 'student');

    const handleClose = () => {
        if (!isLoading) setOpen(false);
    };

    const handleClickDownload = () => {
        downloadCSV(`${course.name}_roster_template.csv`.toLowerCase().replaceAll(' ', '_'), [CSV_HEADERS]);
    };

    const handleClickUpload = () => {
        inputRef.current.click();
    };

    const handleChangeUpload = async (e) => {
        const hasEmptyCell = (parsed) => {
            return parsed.some((row) => row.some((cell) => !cell));
        };

        const getInvalidEmails = (parsed) => {
            const invalids = [];
            parsed.forEach(([email]) => {
                if (!emailRegex.test(email)) {
                    invalids.push(email);
                }
            });
            return invalids;
        };

        const getDuplicateEmails = (parsed) => {
            const duplicates = [];
            parsed.forEach(([email], index, self) => {
                if (self.slice(0, index).some((row) => row[0] === email)) {
                    duplicates.push(email);
                }
            });
            return duplicates;
        };

        let parsed;

        try {
            parsed = await parseCSV(e.target.files[0], CSV_HEADERS, message);
        } catch (e) {
            return console.error(e);
        }

        if (hasEmptyCell(parsed)) {
            return message.error('We ran into an error reading your file. At least one cell is empty.');
        }

        const invalids = getInvalidEmails(parsed);
        if (new Set(invalids).size) {
            return message.error(
                <Text color={'inherit'}>
                    The following email(s) are invalid:{' '}
                    {[...new Set(invalids)].map((email, index) => (
                        <Fragment key={index}>
                            {index ? ', ' : null}
                            <Text color={'inherit'} component={'span'} weight={'semibold'}>
                                {email}
                            </Text>
                        </Fragment>
                    ))}
                </Text>
            );
        }

        const duplicates = getDuplicateEmails(parsed);
        if (new Set(duplicates).size) {
            return message.error(
                <Text color={'inherit'}>
                    The following email(s) appear more than once:{' '}
                    {[...new Set(duplicates)].map((email, index) => (
                        <Fragment key={index}>
                            {index ? ', ' : null}
                            <Text color={'inherit'} component={'span'} weight={'semibold'}>
                                {email}
                            </Text>
                        </Fragment>
                    ))}
                </Text>
            );
        }

        const newCSVStudents = parsed
            .map(([email, firstName, lastName]) => ({ email, firstName, lastName }))
            .filter(({ email }) => {
                const isAlreadyMember = accountUsers.some((accountUser) => accountUser.email === email);
                return !isAlreadyMember;
            });

        if (newCSVStudents.length) {
            setCSVStudents(newCSVStudents);
        } else {
            message.error('All members from the CSV are already in the course.');
        }
    };

    const handleSelect = async (key, student) => {
        if (key === 'copy') {
            await navigator.clipboard.writeText(student.email);
            message.success('Email copied successfully.');
        }
        if (key === 'remove') {
            setCSVStudents((current) => current.filter(({ email }) => email !== student.email));
        }
    };

    const handleClickSend = async () => {
        setIsLoading(true);
        await sendInvitations(
            'student',
            csvStudents.map(({ email, firstName, lastName }) => ({ email, first_name: firstName, last_name: lastName }))
        );
        setAccordions((current) => ({ ...current, invited: true }));
        setCSVStudents([]);
        setIsLoading(false);
        message.success(`${csvStudents.length} members(s) successfully invited.`);
    };

    const CSVInvite = (
        <Stack divider={<Divider />} spacing={4}>
            <Accordion
                sx={{ pt: 4 }}
                expanded={accordions.staged}
                onChange={(_, expanded) => setAccordions((current) => ({ ...current, staged: expanded }))}
            >
                <AccordionSummary>
                    <Text variant={'sh2'} weight={'semibold'}>
                        Invitations to be Sent Out ({csvStudents.length})
                    </Text>
                </AccordionSummary>
                <AccordionDetails>
                    {csvStudents.length ? (
                        <>
                            <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                                <Text color={'light'} variant={'p2'}>
                                    CSV Preview:
                                </Text>
                                <Button variant={'tertiary'} onClick={() => setCSVStudents([])}>
                                    Clear All
                                </Button>
                            </Stack>
                            <InvitedStack device={device} divider={<Divider />}>
                                {csvStudents.map((student, index) => (
                                    <InvitedItemStack
                                        key={student.email}
                                        direction={'row'}
                                        index={index}
                                        justifyContent={'space-between'}
                                        spacing={2}
                                    >
                                        <Text sx={{ mr: 2 }} variant={'p1'}>
                                            {index + 1}
                                        </Text>
                                        <Stack sx={{ flex: 1 }}>
                                            <Text variant={'p1'}>
                                                {student.firstName} {student.lastName}
                                            </Text>
                                            <Text color={'light'} variant={'p2'}>
                                                {student.email}
                                            </Text>
                                        </Stack>
                                        <Dropdown
                                            id={`invite-student-dropdown-${student.email}`}
                                            options={[
                                                { key: 'copy', value: 'Copy Email' },
                                                { key: 'remove', value: 'Remove' },
                                            ]}
                                            rootComponent={
                                                <IconButton
                                                    aria-label={'more options'}
                                                    kind={'moreHorizontal'}
                                                    size={'small'}
                                                    variant={'square'}
                                                />
                                            }
                                            onSelect={({ key }) => handleSelect(key, student)}
                                        />
                                    </InvitedItemStack>
                                ))}
                            </InvitedStack>
                        </>
                    ) : (
                        <Text color={'light'} variant={'p2'}>
                            Please upload a CSV to preview your invitations before sending them out.
                        </Text>
                    )}
                </AccordionDetails>
            </Accordion>
            <Accordion
                expanded={accordions.invited}
                onChange={(_, expanded) => setAccordions((current) => ({ ...current, invited: expanded }))}
            >
                <AccordionSummary>
                    <Text variant={'sh2'} weight={'semibold'}>
                        Students Invited ({studentAccountUsers.length})
                    </Text>
                </AccordionSummary>
                <AccordionDetails>
                    {studentAccountUsers.length ? (
                        <InvitedStack device={device} divider={<Divider />}>
                            {studentAccountUsers.map((accountUser, index) => (
                                <InvitedItemStack
                                    key={accountUser.owner_account_id}
                                    direction={'row'}
                                    index={index}
                                    justifyContent={'space-between'}
                                    spacing={2}
                                >
                                    <Text sx={{ mr: 2 }} variant={'p1'}>
                                        {index + 1}
                                    </Text>
                                    <Stack sx={{ flex: 1 }}>
                                        <Text variant={'p1'}>{getUserPreferredFullName(accountUser)}</Text>
                                        <Text color={'light'} variant={'p2'}>
                                            {accountUser.email}
                                        </Text>
                                    </Stack>
                                    <Dropdown
                                        id={`invite-student-dropdown-${accountUser.owner_account_id}`}
                                        options={getMemberActionOptions(accountUser)}
                                        rootComponent={
                                            <IconButton
                                                aria-label={'more options'}
                                                kind={'moreHorizontal'}
                                                size={'small'}
                                                variant={'square'}
                                            />
                                        }
                                        onSelect={({ key }) => onSelect(key, accountUser)}
                                    />
                                </InvitedItemStack>
                            ))}
                        </InvitedStack>
                    ) : (
                        <Text color={'light'} variant={'p2'}>
                            Course invitations sent out will be shown here.
                        </Text>
                    )}
                </AccordionDetails>
            </Accordion>
            <Stack
                direction={device === 'mobile' ? 'column' : 'row'}
                justifyContent={'space-between'}
                spacing={device === 'mobile' ? 2 : 0}
            >
                <Stack direction={'row'} justifyContent={device === 'mobile' ? 'space-between' : 'unset'} spacing={3}>
                    <Button startIcon={<Icon kind={'download'} />} variant={'tertiary'} onClick={handleClickDownload}>
                        Download CSV Template
                    </Button>
                    <Button startIcon={<Icon kind={'upload'} />} variant={'tertiary'} onClick={handleClickUpload}>
                        {csvStudents.length ? 'Reupload CSV' : 'Upload CSV'}
                    </Button>
                    <input
                        ref={inputRef}
                        style={{ display: 'none' }}
                        accept={'.csv'}
                        type={'file'}
                        onClick={(e) => (e.target.value = null)}
                        onChange={handleChangeUpload}
                    />
                </Stack>
                <Button disabled={!csvStudents.length} loading={isLoading} onClick={handleClickSend}>
                    Send Invite
                </Button>
            </Stack>
        </Stack>
    );

    const hasAlreadyBeenInvited = (email) => {
        const accountUser = accountUsers.find((accountUser) => accountUser.email === email);
        return Boolean(accountUser);
    };

    const handleSubmit = async () => {
        if (!canSubmit || isLoading) return;
        if (inputValue) {
            setEmails((current) => [...current, inputValue]);
            setInputValue('');
        } else {
            const uniqueEmails = [...new Set(emails)];
            const { true: alreadyInvited, false: toBeInvited } = splitArrayByCondition(
                uniqueEmails,
                hasAlreadyBeenInvited
            );
            setIsLoading(true);
            if (toBeInvited.length) {
                await sendInvitations(
                    'student',
                    toBeInvited.map((email) => ({ email }))
                );
            }
            setEmails([]);
            setIsLoading(false);
            if (toBeInvited.length && alreadyInvited.length) {
                message.success(
                    `${toBeInvited.length} members(s) successfully invited, ${alreadyInvited.length} members(s) already in the course.`
                );
            }
            if (!toBeInvited.length && alreadyInvited.length) {
                message.error(`${alreadyInvited.length} members(s) already in the course.`);
            }
            if (toBeInvited.length && !alreadyInvited.length) {
                message.success(`${toBeInvited.length} members(s) successfully invited.`);
            }
        }
    };

    const handleClickChip = (_, targetIndex) => {
        setEmails((current) => current.filter((_, index) => index !== targetIndex));
    };

    const isInputValueValid = emailRegex.test(inputValue);
    const canSubmit =
        isInputValueValid ||
        (Boolean(emails.length) && !Boolean(inputValue) && emails.every((email) => emailRegex.test(email)));

    const EmailInvite = (
        <Stack sx={{ mt: 5 }} divider={<Divider />} spacing={7}>
            <Form onSubmit={handleSubmit}>
                <Stack alignItems={'center'} direction={'row'} spacing={3}>
                    <EmailMultiInput
                        aria-label={'enter email'}
                        inputValue={inputValue}
                        value={emails}
                        onClick={handleClickChip}
                        onChange={setEmails}
                        onChangeInput={(e) => setInputValue(e.target.value)}
                    />
                    <Button disabled={!canSubmit} loading={isLoading} type={'submit'}>
                        Send Invite
                    </Button>
                </Stack>
            </Form>
            <Stack spacing={2}>
                <Text variant={'sh2'} weight={'semibold'}>
                    Students Invited ({studentAccountUsers.length})
                </Text>
                {studentAccountUsers.length ? (
                    <InvitedStack device={device} divider={<Divider />}>
                        {studentAccountUsers.map((accountUser, index) => (
                            <InvitedItemStack
                                key={accountUser.owner_account_id}
                                direction={'row'}
                                index={index}
                                justifyContent={'space-between'}
                                spacing={2}
                            >
                                <Text sx={{ mr: 2 }} variant={'p1'}>
                                    {index + 1}
                                </Text>
                                <Stack sx={{ flex: 1 }}>
                                    <Text variant={'p1'}>{getUserPreferredFullName(accountUser)}</Text>
                                    <Text color={'light'} variant={'p2'}>
                                        {accountUser.email}
                                    </Text>
                                </Stack>
                                <Dropdown
                                    id={`invite-student-dropdown-${accountUser.owner_account_id}`}
                                    options={getMemberActionOptions(accountUser)}
                                    rootComponent={
                                        <IconButton
                                            aria-label={'more options'}
                                            kind={'moreHorizontal'}
                                            size={'small'}
                                            variant={'square'}
                                        />
                                    }
                                    onSelect={({ key }) => onSelect(key, accountUser)}
                                />
                            </InvitedItemStack>
                        ))}
                    </InvitedStack>
                ) : (
                    <Text color={'light'} variant={'p2'}>
                        Course invitations sent out will be shown here.
                    </Text>
                )}
            </Stack>
        </Stack>
    );

    const options = [
        { key: 'csv', value: 'Invite via CSV', content: CSVInvite },
        { key: 'email', value: 'Invite via Email', content: EmailInvite },
    ];

    const [tab, setTab] = useState(options[0].key);

    return (
        <ResponsiveDialog aria-labelledby={'dialog-title'} open={open} onClose={handleClose}>
            <RootStack device={device} spacing={4}>
                <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                    <Text id={'dialog-title'} component={'h1'} variant={'h6'}>
                        Invite Students to the Course
                    </Text>
                    <IconButton
                        aria-label={'close'}
                        disabled={isLoading}
                        kind={'exit'}
                        size={'medium'}
                        variant={'square'}
                        onClick={handleClose}
                    />
                </Stack>
                <Tabs aria-label={'select invitation method'} options={options} selected={tab} onChange={setTab} />
            </RootStack>
        </ResponsiveDialog>
    );
};

const RootStack = styled(Stack)(({ device }) => {
    return {
        width: device === 'mobile' ? '100%' : 620,
    };
});

const InvitedStack = styled(Stack)(({ device }) => {
    return {
        ...(device !== 'mobile' && {
            maxHeight: 325,
            overflowY: 'auto',
        }),
    };
});

const InvitedItemStack = styled(Stack)(({ index, theme }) => {
    return {
        padding: theme.spacing(3),
        background: !(index % 2) ? theme.palette.ensightful.neutral[100] : theme.palette.ensightful.shades[0],
    };
});

export default StudentInvitations;
